import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

/**
 * SEOHead Component for TypoGuard.io
 * Wraps react-helmet-async to manage document head.
 * Enforces XSS protection and consistent meta tags.
 */
const SEOHead = ({
    title,
    description,
    path,
    schema,
    ignoreCanonical = false,
    useTitleTemplate = true,
    fullTitle = null
}) => {
    const siteUrl = 'https://typoguard.io';
    const location = useLocation();

    // Auto-detect path if not provided
    const currentPath = path || location.pathname;
    const fullUrl = `${siteUrl}${currentPath}`;

    // Check for UTM parameters
    const hasUTM = location.search.includes('utm_');

    // If UTM params are present, we WANT the canonical to include them so Umami tracks it.
    // Otherwise, we use the clean path (SEO friendly).
    const canonicalPath = hasUTM ? `${currentPath}${location.search}` : currentPath;
    const finalCanonicalUrl = `${siteUrl}${canonicalPath}`;

    // XSS Protection: Basic sanitization for meta tags
    const sanitize = (str) => {
        if (!str) return '';
        return str.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    };

    const safeTitle = sanitize(title);
    const safeDescription = sanitize(description);

    // Determine the final title
    let finalTitle = '';
    if (fullTitle) {
        finalTitle = sanitize(fullTitle);
    } else if (useTitleTemplate) {
        finalTitle = `${safeTitle} | TypoGuard.io`;
    } else {
        finalTitle = safeTitle;
    }

    return (
        <Helmet>
            <title>{finalTitle}</title>
            <meta name="description" content={safeDescription} />

            {!ignoreCanonical && (
                <link rel="canonical" href={finalCanonicalUrl} />
            )}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={safeDescription} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={fullUrl} />
            <meta property="twitter:title" content={finalTitle} />
            <meta property="twitter:description" content={safeDescription} />

            {/* Structured Data (JSON-LD) */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
        </Helmet>
    );
};

SEOHead.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string.isRequired,
    path: PropTypes.string,
    schema: PropTypes.object,
    ignoreCanonical: PropTypes.bool,
    useTitleTemplate: PropTypes.bool,
    fullTitle: PropTypes.string
};

export default SEOHead;
